import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Users, Calendar, BookOpen, Search, Filter, Trash2 } from "lucide-react";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { getErrorMessage } from "@/lib/errorHandler";
import { toast } from "sonner";
import { useSubjectClasses, useCreateSubjectClass } from "@/hooks/useSubjectClasses";
import { useUserRole } from "@/hooks/useUserRole";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays } from "date-fns";

const SubjectClasses = () => {
  const navigate = useNavigate();
  const { data: roles } = useUserRole();
  const isInstructor = roles?.isInstructor && !roles?.isAdmin && !roles?.isManager && !roles?.isSuperAdmin;
  
  const { data: classes, isLoading } = useSubjectClasses(isInstructor);
  const createClass = useCreateSubjectClass();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canDelete = roles?.isAdmin || roles?.isSuperAdmin;
  
  // Form state for new class
  const [newClass, setNewClass] = useState({
    subject_id: "",
    class_name: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    target_end_date: "",
    notes: "",
    student_ids: [] as string[],
    instructor_id: "",
  });

  // Fetch all subjects (instructors can create classes for any subject)
  const { data: allSubjects } = useQuery({
    queryKey: ["all-subjects"],
    queryFn: async () => {
      const { data } = await supabase
        .from("subjects")
        .select("id, name")
        .order("name");
      return data || [];
    },
  });

  // Fetch all students
  const { data: students } = useQuery({
    queryKey: ["all-students"],
    queryFn: async () => {
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, calling_name")
        .order("first_name");
      return data || [];
    },
  });

  // Fetch all instructors for admin/manager
  const { data: allInstructors } = useQuery({
    queryKey: ["all-instructors"],
    queryFn: async () => {
      const { data } = await supabase
        .from("instructors")
        .select("id, name, branch_id")
        .eq("status", "Active")
        .order("name");
      return data || [];
    },
    enabled: !isInstructor,
  });

  // Fetch current instructor for instructor users
  const { data: currentInstructor } = useQuery({
    queryKey: ["current-instructor"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from("instructors")
        .select("id, branch_id")
        .eq("user_id", user.id)
        .maybeSingle();

      return data;
    },
    enabled: isInstructor,
  });

  const handleCreateClass = async () => {
    if (!newClass.subject_id || !newClass.class_name || !newClass.start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (newClass.student_ids.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    // Determine instructor_id based on user role
    const instructorId = isInstructor ? currentInstructor?.id : newClass.instructor_id;
    const selectedInstructor = isInstructor 
      ? currentInstructor 
      : allInstructors?.find(i => i.id === newClass.instructor_id);

    if (!instructorId || !selectedInstructor) {
      toast.error("Please select an instructor");
      return;
    }

    try {
      await createClass.mutateAsync({
        instructor_id: instructorId,
        subject_id: newClass.subject_id,
        branch_id: selectedInstructor.branch_id || undefined,
        class_name: newClass.class_name,
        start_date: newClass.start_date,
        target_end_date: newClass.target_end_date || undefined,
        notes: newClass.notes || undefined,
        student_ids: newClass.student_ids,
      });

      toast.success("Class created successfully");
      setShowCreateDialog(false);
      setNewClass({
        subject_id: "",
        class_name: "",
        start_date: format(new Date(), "yyyy-MM-dd"),
        target_end_date: "",
        notes: "",
        student_ids: [],
        instructor_id: "",
      });
    } catch (error) {
      toast.error("Failed to create class");
    }
  };

  const handleDeleteClick = (classItem: { id: string; class_name: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to delete "${classItem.class_name}"? This will remove all attendance records and student enrollments.`)) {
      return;
    }
    setClassToDelete({ id: classItem.id, name: classItem.class_name });
    setPinDialogOpen(true);
  };

  const handleDeleteConfirm = async (pin: string) => {
    if (!classToDelete) return;

    try {
      setDeleting(true);

      // Verify PIN
      const { data: isVerified, error: verifyError } = await supabase.rpc('verify_admin_action', {
        _pin: pin,
        _action: 'Delete Subject Class',
        _details: { class_id: classToDelete.id, class_name: classToDelete.name }
      });

      if (verifyError || !isVerified) {
        toast.error(getErrorMessage(verifyError));
        return;
      }

      // Delete class attendance first
      await supabase
        .from('subject_class_attendance')
        .delete()
        .eq('subject_class_id', classToDelete.id);

      // Delete class students
      await supabase
        .from('subject_class_students')
        .delete()
        .eq('subject_class_id', classToDelete.id);

      // Delete the class
      const { error } = await supabase
        .from('subject_classes')
        .delete()
        .eq('id', classToDelete.id);

      if (error) throw error;

      toast.success('Class deleted successfully');
      setPinDialogOpen(false);
      setClassToDelete(null);
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const filteredClasses = classes?.filter(c => {
    const matchesSearch = c.class_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.subject?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.instructor?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active": return "bg-green-500/10 text-green-500 border-green-500/20";
      case "Teaching Complete": return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "Closed": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Subject Classes</h1>
            <p className="text-muted-foreground">Manage your teaching classes and track student progress</p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Class
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Class</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Instructor selector for admin/manager */}
                {!isInstructor && (
                  <div>
                    <Label>Instructor *</Label>
                    <Select
                      value={newClass.instructor_id}
                      onValueChange={(value) => setNewClass({ ...newClass, instructor_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select instructor" />
                      </SelectTrigger>
                      <SelectContent>
                        {allInstructors?.map(instructor => (
                          <SelectItem key={instructor.id} value={instructor.id}>
                            {instructor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Subject *</Label>
                  <Select
                    value={newClass.subject_id}
                    onValueChange={(value) => {
                      const subject = allSubjects?.find(s => s?.id === value);
                      setNewClass({
                        ...newClass,
                        subject_id: value,
                        class_name: subject ? `${subject.name} - ${format(new Date(), "MMM yyyy")}` : "",
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {allSubjects?.map(subject => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Class Name *</Label>
                  <Input
                    value={newClass.class_name}
                    onChange={(e) => setNewClass({ ...newClass, class_name: e.target.value })}
                    placeholder="e.g., Air Law - Dec 2024"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={newClass.start_date}
                      onChange={(e) => setNewClass({ ...newClass, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Target End Date</Label>
                    <Input
                      type="date"
                      value={newClass.target_end_date}
                      onChange={(e) => setNewClass({ ...newClass, target_end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Select Students *</Label>
                  <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                    {students?.map(student => (
                      <label key={student.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newClass.student_ids.includes(student.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewClass({ ...newClass, student_ids: [...newClass.student_ids, student.id] });
                            } else {
                              setNewClass({ ...newClass, student_ids: newClass.student_ids.filter(id => id !== student.id) });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">
                          {student.first_name} {student.last_name}
                          {student.calling_name && ` (${student.calling_name})`}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {newClass.student_ids.length} student(s) selected
                  </p>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={newClass.notes}
                    onChange={(e) => setNewClass({ ...newClass, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>

                <Button onClick={handleCreateClass} className="w-full" disabled={createClass.isPending}>
                  {createClass.isPending ? "Creating..." : "Create Class"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search classes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Teaching Complete">Teaching Complete</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Classes Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredClasses?.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No classes found</h3>
              <p className="text-muted-foreground">Create a new class to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClasses?.map(classItem => {
              const daysElapsed = differenceInDays(new Date(), new Date(classItem.start_date));
              const targetDays = classItem.target_end_date 
                ? differenceInDays(new Date(classItem.target_end_date), new Date(classItem.start_date))
                : null;

              return (
                <Card
                  key={classItem.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/subject-classes/${classItem.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{classItem.class_name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{classItem.subject?.name}</p>
                      </div>
                      <Badge variant="outline" className={getStatusColor(classItem.status)}>
                        {classItem.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{classItem.students_count || 0} students</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>{classItem.total_class_days} class days</span>
                        </div>
                      </div>

                      <div className="text-sm">
                        <span className="text-muted-foreground">Started: </span>
                        <span>{format(new Date(classItem.start_date), "MMM d, yyyy")}</span>
                        <span className="text-muted-foreground ml-2">({daysElapsed} days ago)</span>
                      </div>

                      {targetDays && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Progress: </span>
                          <span>Day {classItem.total_class_days} / {targetDays} target</span>
                        </div>
                      )}

                      {!isInstructor && classItem.instructor && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Instructor: </span>
                          <span>{classItem.instructor.name}</span>
                        </div>
                      )}

                      {canDelete && (
                        <div className="flex justify-end pt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={(e) => handleDeleteClick(classItem, e)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <PinVerificationDialog
          open={pinDialogOpen}
          onOpenChange={setPinDialogOpen}
          onConfirm={handleDeleteConfirm}
          title="Delete Subject Class"
          description={`Enter your PIN to confirm deletion of "${classToDelete?.name}"`}
          confirmLabel="Delete Class"
          loading={deleting}
        />
      </div>
    </Layout>
  );
};

export default SubjectClasses;
